import type { IVec, PointLocation } from '@blocksuite/global/gfx';
import { Bound, linePolygonIntersects } from '@blocksuite/global/gfx';
import type {
  BaseElementProps,
  GfxModel,
  SerializedElement,
} from '@blocksuite/std/gfx';
import {
  canSafeAddToContainer,
  field,
  GfxGroupLikeElementModel,
  local,
} from '@blocksuite/std/gfx';
import * as Y from 'yjs';

type GroupElementProps = BaseElementProps & {
  title: Y.Text;
};

export type SerializedGroupElement = SerializedElement & {
  title: string;
  children: Record<string, boolean>;
};

/**
 * A group of canvas elements and blocks. Its members are the g of their
 * structure entries; a new group names them in its `children` prop.
 */
export class GroupElementModel extends GfxGroupLikeElementModel<GroupElementProps> {
  static override readonly structureMembersProp = 'children';

  get rotate() {
    return 0;
  }

  set rotate(_: number) {}

  get type() {
    return 'group';
  }

  static propsToY(props: Record<string, unknown>) {
    if (typeof props.title === 'string') {
      props.title = new Y.Text(props.title as string);
    }

    return props as GroupElementProps;
  }

  override addChild(element: GfxModel) {
    if (!canSafeAddToContainer(this, element)) {
      return;
    }

    this.surface.store.transact(() => {
      this.surface.store.pageStructure.join(element.id, this.id);
    });

    // Set parent reference for O(1) lookup
    if ('_parentGroupId' in element) {
      element._parentGroupId = this.id;
    }
  }

  override containsBound(bound: Bound): boolean {
    return bound.contains(Bound.deserialize(this.xywh));
  }

  override getLineIntersections(
    start: IVec,
    end: IVec
  ): PointLocation[] | null {
    const bound = Bound.deserialize(this.xywh);
    return linePolygonIntersects(start, end, bound.points);
  }

  removeChild(element: GfxModel) {
    if (!this.hasChild(element)) {
      return;
    }
    this.surface.store.transact(() => {
      this.surface.store.pageStructure.leave(element.id);
    });

    // Clear parent reference
    if ('_parentGroupId' in element) {
      element._parentGroupId = null;
    }
  }

  override *serializeWork(): Generator<void, SerializedGroupElement> {
    const result = (yield* super.serializeWork()) as SerializedGroupElement;
    result.children = Object.fromEntries(this.childIds.map(id => [id, true]));
    return result;
  }

  override serialize() {
    return super.serialize() as SerializedGroupElement;
  }

  override syncMembers(local: boolean) {
    const members = this.surface.store.pageStructure.members(this.id);
    if (
      members.length !== this.childIds.length ||
      members.some((id, at) => id !== this.childIds[at])
    ) {
      this.setChildIds([...members], local);
    }
  }

  override lock(): void {
    super.lock();
    this.showTitle = false;
  }

  override unlock(): void {
    super.unlock();
    this.showTitle = true;
  }

  @local()
  accessor showTitle: boolean = true;

  @field()
  accessor title: Y.Text = new Y.Text();
}
